/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { FunctionComponent, useMemo, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { ITask } from '../../../../../app/resource/task/task.types';
import { userSelectors } from '../../../../../app/resource/user/user.selectors';
import { useRequiredValidation } from '../../../../../utils/form/validation.utils';
import { useSelect } from '../../../../../utils/selector.utils';
import { DateTimeInput } from '../../inputs/DateTimeInput';
import { SelectInput } from '../../inputs/SelectInput';
import { FormTextInput } from '../../inputs/TextInput';
import { ITaskFormProps } from './types';
import moment from 'moment';
import { meetingSimpleSelectors } from '../../../../../app/resource/meeting-simple/meeting-simple.selectors';
import { IMeetingSimple } from '../../../../../app/resource/meeting-simple/meeting-simple.types';
import { ITaskSimpleCreateForm } from '../../../../../app/resource/task-simple/task-simple.types';
import {
  createFormTaskSimple,
  updateFormTaskSimple,
} from '../../../../../app/page/page-storage/tasks-simple/tasks-simple.actions';
import { authSelectors } from '../../../../../app/auth/auth.selectors';
import { getGoalAssignableUsers, getGoalMeetingOptions } from '../../../../../utils/user.utils';
import { IUser } from '../../../../../app/resource/user/user.types';
import { annualsSelectors } from '../../../../../app/resource/annuals/annuals.selectors';
import { sortBy } from 'lodash';
import { findCurrentUtcRange, isUtcDateOnOrAfterToday } from '../../../../../utils/date.utils';

const GOAL_TASK_TYPE_ID = '1';

export const TaskForm: FunctionComponent<ITaskFormProps> = ({ task, userId, meetingId }) => {
  const dispatch = useDispatch();
  const formProps = useForm();
  const taskQuarterId = task?.quarterId;
  const taskUserId = task?.userId;

  const requiredValidation = useRequiredValidation();
  const loggedUserId = (useSelect(authSelectors.selectLoggedUser) || '') as IUser['id'];
  const isLoggedUserSuperAdmin = useSelect(authSelectors.selectUserIsAdmin);

  const annuals = useSelect(annualsSelectors.selectResourceList);

  const quarterOptions = useMemo(
    () =>
      sortBy(annuals, ['year']).flatMap((annual) =>
        annual.quarters
          .filter((quarter) => quarter.id === taskQuarterId || isUtcDateOnOrAfterToday(quarter.endDate))
          .map((quarter) => ({
            value: quarter.id,
            label: `P${quarter.number} ${annual.year}`,
          })),
      ),
    [annuals, taskQuarterId],
  );
  const currentQuarter = useMemo(() => findCurrentUtcRange(annuals.flatMap((annual) => annual.quarters)), [annuals]);
  const quarterDefaultOption = useMemo(() => {
    if (taskQuarterId) {
      return quarterOptions.find((option) => option.value === taskQuarterId);
    }

    if (!task && currentQuarter) {
      return quarterOptions.find((option) => option.value === currentQuarter.id);
    }

    return undefined;
  }, [currentQuarter, quarterOptions, task, taskQuarterId]);

  const [isQuarter, setIsQuarter] = useState(task ? !!task.quarterId : false);
  const hasPeriodOptions = quarterOptions.length > 0;
  useEffect(() => {
    if (!task) {
      setIsQuarter(hasPeriodOptions);
    }
  }, [hasPeriodOptions, task]);

  const onSubmit = (data: ITaskSimpleCreateForm, taskId: ITask['id'] | undefined) => {
    if (!taskId) {
      dispatch(createFormTaskSimple({ ...data, taskTypeId: GOAL_TASK_TYPE_ID }));
    } else {
      dispatch(updateFormTaskSimple({ ...data, taskTypeId: GOAL_TASK_TYPE_ID, id: taskId }));
    }
  };

  const users = useSelect(userSelectors.selectResourceList);
  const meetings = useSelect(meetingSimpleSelectors.selectResourceList);

  const resetDependentsFieldsFor = () => {
    formProps.setValue('meeting', '');
  };

  const focusUserTuple = formProps.watch('user');
  const focusUserId = focusUserTuple?.value as IUser['id'] | undefined;
  const goalUsers = useMemo(
    () =>
      getGoalAssignableUsers({
        users,
        meetings,
        loggedUserId,
        isSuperAdmin: !!isLoggedUserSuperAdmin,
        currentAssigneeId: taskUserId,
      }),
    [isLoggedUserSuperAdmin, loggedUserId, meetings, taskUserId, users],
  );

  const [selectedUser, userOptions] = useMemo(() => {
    const mappedUsers = sortBy(
      goalUsers.map((user) => ({
        value: user.id,
        label: `${user.firstName} ${user.lastName}`,
      })),
      ['label'],
    );

    let foundUser: { value: string; label: string } | undefined = undefined;

    if (focusUserTuple) {
      foundUser = mappedUsers.find((option) => option.value === focusUserTuple.value);
    } else if (!task) {
      foundUser = mappedUsers.find((option) => option.value === loggedUserId);
    } else {
      foundUser = mappedUsers.find((option) => option.value === (taskUserId ?? userId));
    }

    return [foundUser, mappedUsers];
  }, [focusUserTuple, goalUsers, loggedUserId, task, taskUserId, userId]);

  const selectedUserId = (focusUserId ?? selectedUser?.value) as IUser['id'] | undefined;

  const [selectedMeeting, meetingOptions] = useMemo(() => {
    const meetingsForSelectedUser = getGoalMeetingOptions({
      meetings,
      users,
      loggedUserId,
      selectedUserId,
      isSuperAdmin: !!isLoggedUserSuperAdmin,
      currentMeetingIds: task?.meetingIds,
    });

    const mappedMeetings = sortBy(
      meetingsForSelectedUser.map((meeting) => ({
        value: meeting.id,
        label: `${meeting.title}`,
      })),
      ['label'],
    );

    let foundMeetings: { value: string; label: string }[] | undefined = undefined;

    if (task) {
      foundMeetings = sortBy(
        (meetings.filter((team) => task.meetingIds.includes(team.id)) as IMeetingSimple[]).map((team) => ({
          value: team.id,
          label: team.title,
        })),
        ['label'],
      );
      foundMeetings = foundMeetings.length !== 0 ? foundMeetings : undefined;
    } else if (meetingId) {
      const defaultMeeting = mappedMeetings.find((option) => option.value === meetingId);
      foundMeetings = defaultMeeting ? [defaultMeeting] : undefined;
    }

    return [foundMeetings, mappedMeetings];
  }, [isLoggedUserSuperAdmin, loggedUserId, meetingId, meetings, selectedUserId, task, users]);

  const reminderOptions = [
    { value: 'noReminder', label: 'None' },
    { value: 'everyDay', label: 'Every Day' },
    { value: 'everyWeek', label: 'Every Week' },
    { value: 'everyMonth', label: 'Every Month' },
  ];

  const rockStatusOptions = [
    { value: '0', label: 'On Track' },
    { value: '2', label: 'Off Track' },
    { value: '1', label: 'Done' },
    { value: '3', label: 'Incomplete' },
  ];

  const defaultReminder = task?.reminder
    ? reminderOptions.filter((option) => option.value === task?.reminder)
    : reminderOptions.filter((option) => option.value === 'noReminder');

  const defaultRockStatus = task
    ? rockStatusOptions.filter((option) => option.value === task?.status)[0]
    : rockStatusOptions[0];

  const defaultValue = task?.dueDate;
  const date = useMemo(() => {
    if (!defaultValue) {
      return undefined;
    }

    try {
      const dateString = moment(defaultValue).format('YYYY-MM-DD HH:mm:ss');
      return new Date(dateString);
    } catch (error) {
      console.log(error);
    }
  }, [defaultValue]);

  return (
    <FormProvider {...formProps}>
      <form
        id="task-form"
        className="topic-form"
        onSubmit={formProps.handleSubmit((formData) => onSubmit(formData as ITaskSimpleCreateForm, task?.id))}
        method="post"
      >
        <div className="field-container">
          <FormTextInput
            id="title"
            name="title"
            type="text"
            label="Title"
            placeholder="Enter Title"
            defaultValue={task ? task.title : ''}
            validation={requiredValidation}
          />
        </div>
        <div className="field-container">
          <SelectInput
            id="user"
            name="user"
            label="User"
            placeholder="Search..."
            defaultOptions={selectedUser}
            validation={requiredValidation}
            onInputChange={resetDependentsFieldsFor}
            options={userOptions}
            isSingle
          />
        </div>

        <div className="field-container">
          <SelectInput
            id="meeting"
            name="meeting"
            label="Meeting"
            placeholder="Search..."
            defaultOptions={selectedMeeting}
            validation={requiredValidation}
            options={meetingOptions}
            disabled={!selectedUserId}
          />
        </div>
        {hasPeriodOptions && (
          <div className="field-container flex justify-content-center mt-4 themed-radio">
            <div className="mr-2">
              <input
                id="quarter"
                type="radio"
                name="due-date-type"
                checked={isQuarter === true}
                onChange={() => setIsQuarter(true)}
              />
              <label htmlFor="quarter">Period</label>
            </div>
            <div>
              <input
                id="custom"
                type="radio"
                name="due-date-type"
                checked={isQuarter === false}
                onChange={() => setIsQuarter(false)}
              />
              <label htmlFor="custom">Custom</label>
            </div>
          </div>
        )}
        <div className="field-container">
          {hasPeriodOptions && isQuarter ? (
            <SelectInput
              id="quarter"
              name="quarter"
              label="Period"
              placeholder="Select period..."
              validation={requiredValidation}
              options={quarterOptions}
              defaultOptions={quarterDefaultOption}
              isSingle
            />
          ) : (
            <DateTimeInput
              id="dueDate"
              name="dueDate"
              label="Due Date"
              placeholder="Enter Start Date"
              defaultValue={date}
              validation={requiredValidation}
            />
          )}
        </div>
        <div className="field-container custom-select with-checkbox">
          <SelectInput
            id="status"
            name="status"
            label="Status"
            placeholder="Select..."
            options={rockStatusOptions}
            validation={requiredValidation}
            defaultOptions={defaultRockStatus}
            isSingle
          />
        </div>

        <div className="field-container">
          <SelectInput
            id="reminder"
            name="reminder"
            label="Reminder"
            placeholder="Select..."
            options={reminderOptions}
            defaultOptions={defaultReminder}
            isSingle
          />
        </div>

        <div className="field-container">
          <FormTextInput
            id="description"
            name="description"
            label="Description"
            placeholder="Enter Description"
            defaultValue={task ? task.description : ''}
            multi
          />
        </div>
      </form>
    </FormProvider>
  );
};
